/**
 * MANAGEMENT (14) / Support — formatting + tone helpers for the Support SCREEN, ADDITIVE to the shell's
 * lib.ts (which owns `ticketStatutTone` + the date formatters). This file adds the priority/category
 * mappers + the chat-bubble side resolver. Kept out of a screen file so the list + chat panel share them
 * without tripping the react-refresh single-export rule.
 */
import type { BadgeTone } from "@/ui";
import type { TicketCategorie, TicketPriorite } from "./types";
import type { MessageAuthorType } from "./support.types";

/**
 * Badge tone per support-ticket priority. Higher SLA tier = hotter tone (carried WITH text/glyph, never
 * color-only — daltonisme + médico-légal): urgente → danger · haute → warn · normale → info · basse → neutral.
 */
export function prioriteTone(priorite: TicketPriorite | null | undefined): BadgeTone {
  switch (priorite) {
    case "urgente":
      return "danger";
    case "haute":
      return "warn";
    case "normale":
      return "info";
    case "basse":
      return "neutral";
    default:
      return "neutral";
  }
}

/** A small shape glyph per priority so the tier reads without relying on color alone. */
export function prioriteGlyph(priorite: TicketPriorite | null | undefined): string {
  switch (priorite) {
    case "urgente":
      return "▲▲";
    case "haute":
      return "▲";
    case "normale":
      return "■";
    case "basse":
      return "▽";
    default:
      return "■";
  }
}

/** The i18n key for a priority label (FR/AR registered in support.messages.ts). */
export function prioriteLabelKey(priorite: TicketPriorite): string {
  return `mgmt.support.priorite.${priorite}`;
}

/** The i18n key for a category label. */
export function categorieLabelKey(categorie: TicketCategorie): string {
  return `mgmt.support.categorie.${categorie}`;
}

/** The i18n key for a status label (mirrors the shell's ticketStatutTone domain). */
export function statutLabelKey(statut: string): string {
  return `mgmt.support.statut.${statut}`;
}

/**
 * Which side of the chat a message sits on, from the cross-lab OPERATOR's point of view: the operator's
 * own (`admin`) messages are outgoing (end-aligned); a lab's (`labo`) are incoming (start-aligned).
 */
export function isOwnMessage(auteurType: MessageAuthorType): boolean {
  return auteurType === "admin";
}
